import type { ComponentType } from 'react'
import {
  BookOpen,
  CalendarDays,
  FlaskConical,
  FlaskConicalOff,
  Handshake,
  Heart,
  Link2,
  Megaphone,
  Newspaper,
  Presentation,
  Repeat,
  Shapes,
  Store,
  type LucideProps,
} from 'lucide-react'
import {
  fazaActivitatii,
  type FazaActivitate,
  type StareContainer,
  type TipCampanie,
} from '@b4l/shared'
import type { Ton } from '../../ui/TonuriB4L'
import type { Campanie } from './comun'

/**
 * Piesele comune ale unei activitati din PR & Evenimente: iconita tipului, faza cu tonul ei,
 * semnalele „ce cere atentie" si bugetul. Le folosesc lista, panoul lateral si calendarul, ca
 * o activitate sa arate la fel oriunde apare.
 */

export const TON_FAZA: Record<FazaActivitate, Ton> = {
  propunere: 'gri',
  pregatire: 'albastru',
  desfasurare: 'mov',
  de_inchis: 'galben',
  incheiata: 'verde',
  anulata: 'gri',
}

const ICON_TIP: Record<TipCampanie, ComponentType<LucideProps>> = {
  campanie: Megaphone,
  eveniment: CalendarDays,
  program: Repeat,
  publicatie: BookOpen,
  spatiu_publicitar: Presentation,
  articol_presa: Newspaper,
  barter_social_media: Handshake,
  cross_marketing: Link2,
  sponsorizare: Heart,
  expunere: Store,
  altele: Shapes,
}

export function IconTip({ tip, size = 16 }: { tip: TipCampanie; size?: number }) {
  const I = ICON_TIP[tip] ?? Shapes
  return <I size={size} aria-hidden />
}

/*
  Culoarea fiecarui tip — fundalul si iconita patratelului de pe rand (Madalin, 15.10.2026).
  Tipurile inrudite au aceeasi culoare: campanie si cross-marketing, publicatie si articol de
  presa. Clasele sunt scrise intregi, ca Tailwind sa le gaseasca.
*/
export const CULOARE_TIP: Record<TipCampanie, string> = {
  eveniment: 'bg-[rgb(var(--b4l-tip-albastru-bg))] text-[rgb(var(--b4l-tip-albastru))]',
  campanie: 'bg-[rgb(var(--b4l-tip-indigo-bg))] text-[rgb(var(--b4l-tip-indigo))]',
  cross_marketing: 'bg-[rgb(var(--b4l-tip-indigo-bg))] text-[rgb(var(--b4l-tip-indigo))]',
  sponsorizare: 'bg-[rgb(var(--b4l-tip-roz-bg))] text-[rgb(var(--b4l-tip-roz))]',
  barter_social_media: 'bg-[rgb(var(--b4l-tip-violet-bg))] text-[rgb(var(--b4l-tip-violet))]',
  spatiu_publicitar: 'bg-[rgb(var(--b4l-tip-turcoaz-bg))] text-[rgb(var(--b4l-tip-turcoaz))]',
  expunere: 'bg-[rgb(var(--b4l-tip-oliv-bg))] text-[rgb(var(--b4l-tip-oliv))]',
  publicatie: 'bg-b4l-coral-light text-b4l-coral',
  articol_presa: 'bg-b4l-coral-light text-b4l-coral',
  program: 'bg-b4l-bg text-b4l-text-muted',
  altele: 'bg-b4l-bg text-b4l-text-muted',
}

/** Patratelul colorat al tipului — acelasi pe rand, in panou si pe pagina activitatii. */
export function PatratTip({
  tip,
  eticheta,
  mare = true,
}: {
  tip: TipCampanie
  eticheta: string
  mare?: boolean
}) {
  return (
    <span
      title={eticheta}
      className={`grid shrink-0 place-items-center rounded-lg ${mare ? 'h-[34px] w-[34px]' : 'h-6 w-6'} ${
        CULOARE_TIP[tip] ?? CULOARE_TIP.altele
      }`}
    >
      <IconTip tip={tip} size={mare ? 16 : 13} />
    </span>
  )
}

/**
 * Daca activitatea are mostre — iconita pastrata din coloana „Mostre" (Madalin, 15.10.2026:
 * „sa se vada direct daca evenimentul/campania necesita mostre sau nu").
 */
export function IconMostre({ are, cuText = false }: { are: boolean; cuText?: boolean }) {
  const text = are ? 'Are mostre' : 'Fără mostre'
  return (
    <span className="inline-flex items-center gap-1.5" title={text}>
      <span
        role="img"
        aria-label={text}
        className={`inline-grid h-7 w-7 place-items-center rounded-lg ${
          are ? 'bg-b4l-green-light text-b4l-green-dark' : 'text-b4l-text-dim'
        }`}
      >
        {are ? <FlaskConical size={15} /> : <FlaskConicalOff size={15} />}
      </span>
      {cuText && <span className="text-mic text-b4l-text-muted">{text.toLowerCase()}</span>}
    </span>
  )
}

/** Activitatea cu faza ei, calculata o data. */
export type ActivitateCuFaza = Campanie & { faza: FazaActivitate }

export function cuFaza(c: Campanie, azi = new Date()): ActivitateCuFaza {
  return {
    ...c,
    faza: fazaActivitatii(
      c.status as StareContainer,
      new Date(c.dataStart),
      c.dataFinal ? new Date(c.dataFinal) : null,
      azi,
      c.tip,
      c.generatAutomat,
    ),
  }
}

function zileIntre(a: Date, b: Date): number {
  const z = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()
  return Math.round((z(b) - z(a)) / 86_400_000)
}

/** „peste 7 zile", „în curs · încă 3 zile", „s-a terminat acum 5 zile". */
export function candRelativ(c: Campanie, azi = new Date()): string {
  const start = zileIntre(azi, new Date(c.dataStart))
  if (start > 0) return start === 1 ? 'mâine' : `peste ${start} zile`
  // Fara final: a inceput si tine pe termen nedefinit — nu „s-a terminat".
  if (!c.dataFinal) return start === 0 ? 'începe azi · fără final' : 'în curs · fără dată de final'
  const final = zileIntre(azi, new Date(c.dataFinal))
  if (final >= 0) return final === 0 ? 'ultima zi azi' : `în curs · încă ${final + 1} zile`
  return final === -1 ? 's-a terminat ieri' : `s-a terminat acum ${-final} zile`
}

export interface Semnal {
  ton: Ton
  text: string
  /** Explicatia de la trecerea mouse-ului. */
  detaliu?: string
}

/**
 * Ce cere atentie la o activitate, cel mai grav intai. Rosu doar ce blocheaza (o cerere care
 * asteapta un admin); galben ce trebuie verificat.
 */
export function semnale(a: ActivitateCuFaza, azi = new Date()): Semnal[] {
  const s: Semnal[] = []
  const n = a.cereriDeAprobat ?? 0
  if (n > 0)
    s.push({
      ton: 'rosu',
      text: n === 1 ? 'cerere de marfă de aprobat' : `${n} cereri de marfă de aprobat`,
    })
  if (a.faza === 'de_inchis') {
    const z = -zileIntre(azi, new Date(a.dataFinal ?? a.dataStart))
    s.push({
      ton: 'galben',
      text: z <= 1 ? 'de închis' : `de închis de ${z} zile`,
      detaliu: 'S-a terminat, dar n-a fost închisă.',
    })
  }
  if (a.cerereUitata)
    s.push({
      ton: 'galben',
      text: 'marfă neridicată',
      detaliu: 'O cerere aprobată de peste o săptămână din care n-a plecat toată marfa.',
    })
  if (a.plata === 'in_asteptare')
    s.push({ ton: 'galben', text: 'plată în așteptare', detaliu: a.plataMotiv ?? undefined })
  return s
}

export function lei(v: number): string {
  return `${v.toLocaleString('ro-RO', { maximumFractionDigits: 0 })} lei`
}

/** Bugetul, cu formula paginii activitatii: disponibil = buget − angajat − cheltuit. */
export function bani(a: Campanie) {
  const buget = a.bugetAlocat == null ? null : Number(a.bugetAlocat)
  const cheltuit = a.costTotal ?? 0
  const angajat = a.angajat ?? 0
  return {
    buget,
    cheltuit,
    angajat,
    disponibil: buget == null ? null : buget - angajat - cheltuit,
    folosit: buget ? Math.round(((cheltuit + angajat) / buget) * 100) : null,
  }
}

export function BaraBuget({ a, inalta = false }: { a: Campanie; inalta?: boolean }) {
  const b = bani(a)
  if (!b.buget) return null
  const p = (v: number) => `${Math.min(100, Math.max(0, (v / b.buget!) * 100))}%`
  return (
    <div
      role="img"
      aria-label={`${lei(b.cheltuit)} cheltuit, ${lei(b.angajat)} angajat, din ${lei(b.buget)}`}
      className={`flex overflow-hidden rounded-full bg-b4l-bg ${inalta ? 'h-2' : 'h-1.5'}`}
    >
      <i className="block bg-b4l-blue" style={{ width: p(b.cheltuit) }} />
      <i className="block bg-b4l-blue/35" style={{ width: p(b.angajat) }} />
    </div>
  )
}
